import axios, { AxiosResponse } from 'axios'
import { IhmLoginTokenProvider } from '@api/utils/IhmLoginTokenProvider'
import { LatencyMetric } from '@amzn/ihm-associate-tools-js-lib'
import { TokenUtils } from '@utils/TokenUtils'

const IhmCandyCaneAjaxRequestConstants = Object.freeze({
    // We redirect to logout if we see these exceptions
    IMMEDIATE_LOGOUT_EXCEPTIONS: Object.freeze([
        'com.amazon.coral.service#IncompleteSignatureException',
    ]),

    // We verify the token and make sure it's expired before redirecting to logout
    VERIFY_TOKEN_LOGOUT_EXCEPTIONS: Object.freeze([
        'com.amazon.sentry.sso#SentryRedirectException',
    ]),
    ABOUT_BLANK_PAGE: 'about:blank',
    MAX_LOGOUT_ATTEMPTS: 5,
})

interface IIhmCandyCaneAjaxRequestOptions {
    sensitive?: boolean | false,
    data?: Record<string | symbol, any>,
    [key: string]: any,
}

export class IhmCandyCaneAjaxRequestHandler {
    private static logoutAttempts = 0

    /**
     * Makes the request to the appropriate endpoint. These calls come as CrossFrame calls from CrookHookCore.
     * This function sends the request to the appropriate service, inspects the response to decide if it should
     * redirect to logout and serializes the response so that the caller can use it.
     */
    public static async listener (input): Promise<string> {
        console.debug('Making request ',
            IhmCandyCaneAjaxRequestHandler.getDataForLoggingEvent(input.sensitive, input.data.requestData))

        const headers = JSON.parse(JSON.stringify(input.data.requestHeaders))
        // Poor man's clone; no underscore available here?

        const useMetadataResponse = input.data.requestExtra && input.data.requestExtra.useMetadataResponse || false

        const authorizationHeaderString = [
            await IhmLoginTokenProvider.getTokenType(),
            await IhmLoginTokenProvider.getAccessToken(),
        ].join(' ')

        headers['Content-Type'] = 'application/json; charset=UTF-8'
        headers['Content-Encoding'] = 'amz-1.0'
        headers.Authorization = authorizationHeaderString

        const ajaxRequestLatencyMetric = new LatencyMetric(undefined, 'AuthenticatedRequestLatencyMetric', 'IhmFlyingDutchman', 'IhmCandyCaneAjaxRequestHandler')
        ajaxRequestLatencyMetric.start()
        return axios({
            method: 'POST',
            url: input.data.endpoint,
            data: input.data.requestData,
            headers,
            withCredentials: true,
        }).then(async (response: AxiosResponse) => {
            ajaxRequestLatencyMetric.stop()
            const data = await IhmCandyCaneAjaxRequestHandler.processResponse(response, { useMetadataResponse })
            console.debug('Successful response',
                IhmCandyCaneAjaxRequestHandler.getDataForLoggingEvent(input.sensitive, data))
            return data
        }).catch(async (catchResponse) => {
            ajaxRequestLatencyMetric.stop()
            const data = await IhmCandyCaneAjaxRequestHandler.processResponse(catchResponse.response, { useMetadataResponse })
            console.warn('Error response',
                IhmCandyCaneAjaxRequestHandler.getDataForLoggingEvent(input.sensitive, catchResponse))
            console.warn('Error data',
                IhmCandyCaneAjaxRequestHandler.getDataForLoggingEvent(input.sensitive, data))
            return Promise.reject(data)
        })
    }

    // private functions

    /**
     * Inspects the response to see if it should redirect to sign in. Then, it extracts the data from the response and
     * serializes it as JSON. If the response doesn't contain data, it serializes the whole response instead.
     */
    private static async processResponse (response: AxiosResponse, options: {[key: string]: any}): Promise<string> {
        const useMetadataResponse = options && options.useMetadataResponse || false

        if (await IhmCandyCaneAjaxRequestHandler.shouldRedirectToSignIn(response)) {
            IhmCandyCaneAjaxRequestHandler.redirectToSignIn()
        }

        let processedResponse = (response.hasOwnProperty('data')) ? response.data : response

        if (useMetadataResponse) {
            processedResponse = {
                metadata: {
                    hasMetadata: true,
                    statusCode: response.status,
                },
                data: processedResponse,
            }
        }

        // ihmLoginServiceModule expects a stringified JSON
        return JSON.stringify(processedResponse)
    }

    /**
     * Inspects the response and decides if we should redirect to logout
     */
    private static async shouldRedirectToSignIn (response: AxiosResponse): Promise<boolean> {
        if (!response || !response.data || !response.data.__type) {
            return false
        }

        const exceptionType = response.data.__type
        console.debug('Matching', exceptionType, 'against logout exceptions')

        if (IhmCandyCaneAjaxRequestConstants.IMMEDIATE_LOGOUT_EXCEPTIONS.indexOf(exceptionType) >= 0) {
            return true
        }

        if (IhmCandyCaneAjaxRequestConstants.VERIFY_TOKEN_LOGOUT_EXCEPTIONS.indexOf(exceptionType) === -1) {
            return false
        }

        const accessToken = await IhmLoginTokenProvider.getAccessToken()

        if (!accessToken) {
            console.warn('No access token found')
            return true
        }

        if (!TokenUtils.isTokenExpired(accessToken)) {
            console.warn('Got exception', response.data.__type, 'but token was still valid')
            return false
        }

        return true
    }

    /**
     * Attempts to redirect to the sign in page. It used information from the device to determine if the app is running
     * or paused and, based on that, decides if it should kill the app (when paused) or go through the normal logout
     * flow. The reason we can't always go through the normal flow is because Android prevents page navigations after
     * certain inactivity time (unless they're to about:blank).
     *
     * We also keep track of the logout attempts because we've observed that sometimes navigating to logout isn't
     * actually logging out.
     */
    private static redirectToSignIn (): void {
        console.debug('Redirecting to signin. Logout attempts', IhmCandyCaneAjaxRequestHandler.logoutAttempts)
        // TODO: If condition "|| pauseResumeEventHandler.isPaused()" after pauseResumeEventHandler crossFrameListener
        // get added
        if (IhmCandyCaneAjaxRequestHandler.logoutAttempts >= IhmCandyCaneAjaxRequestConstants.MAX_LOGOUT_ATTEMPTS) {
            // Causes the app to exit
            console.debug('App was paused or max logout attempts exceeded so redirecting to',
                IhmCandyCaneAjaxRequestConstants.ABOUT_BLANK_PAGE)
            window.location.assign(IhmCandyCaneAjaxRequestConstants.ABOUT_BLANK_PAGE)
        } else {
            console.debug('App in the foreground redirecting to logout')
            window.location.hash = '#/logout?keep_username=false'
        }
        IhmCandyCaneAjaxRequestHandler.logoutAttempts++
    }

    /**
     * Returns a redacted string if `sensitive` is set to true. Otherwise it returns the data
     * (doesn't convert it to JSON).
     *
     * @param {boolean} sensitive - true if `data` contains sensitive information that shouldn't be logged.
     * @param {any} data - data that could be sensitive.
     *
     * @return {any | string} returns the `data` if `sensitive` is false or a redacted string if it's `true`.
     */
    private static getDataForLoggingEvent (sensitive: boolean, data: string): string {
        if (sensitive) {
            return '<-- Redacted since sensitive=true -->'
        }
        return data
    }
}
